"use client";
import { isElementInside, isElementMarked } from "./tree-cleanup.js";
import { usePreviousMouseDownRef } from "./use-previous-mouse-down-ref.js";
import { useEvent, useSafeLayoutEffect } from "@ariakit/react-utils";
import { addGlobalEventListener, contains, getDocument, getWindow, isElement } from "@ariakit/utils";
import { useEffect, useRef } from "react";
import { useStoreState } from "@ariakit/react-store";
//#region src/dialog/utils/use-hide-on-interact-outside.ts
function isInDocument(target) {
	if (target.tagName === "HTML") return true;
	return contains(getDocument(target).body, target);
}
function isDisclosure(disclosure, target) {
	if (!disclosure) return false;
	if (contains(disclosure, target)) return true;
	const activeId = target.getAttribute("aria-activedescendant");
	if (activeId) {
		const activeElement = getDocument(disclosure).getElementById(activeId);
		if (activeElement) return contains(disclosure, activeElement);
	}
	return false;
}
function isMouseEventOnDialog(event, dialog) {
	if (!("clientY" in event)) return false;
	const rect = dialog.getBoundingClientRect();
	if (rect.width === 0 || rect.height === 0) return false;
	return rect.top <= event.clientY && event.clientY <= rect.top + rect.height && rect.left <= event.clientX && event.clientX <= rect.left + rect.width;
}
function useEventOutside({ store, type, listener, capture, open, contentElement, focusedRef }) {
	const callListener = useEvent(listener);
	useEffect(() => {
		if (!open) return;
		const onEvent = (event) => {
			const { contentElement, disclosureElement } = store.getState();
			const target = event.target;
			if (!contentElement) return;
			if (!isElement(target)) return;
			if (!isInDocument(target)) return;
			if (contains(contentElement, target)) return;
			if (isDisclosure(disclosureElement, target)) return;
			if (target.hasAttribute("data-focus-trap")) return;
			if (isMouseEventOnDialog(event, contentElement)) return;
			if (isElementInside(target, contentElement.id)) return;
			if (focusedRef.current && !isElementMarked(target, contentElement.id)) return;
			callListener(event);
		};
		return addGlobalEventListener(type, onEvent, capture, contentElement ? getWindow(contentElement) : void 0);
	}, [
		open,
		capture,
		store,
		type,
		callListener,
		contentElement,
		focusedRef
	]);
}
function shouldHideOnInteractOutside(hideOnInteractOutside, event) {
	if (typeof hideOnInteractOutside === "function") return hideOnInteractOutside(event);
	return !!hideOnInteractOutside;
}
function useHideOnInteractOutside(store, hideOnInteractOutside, domReady, interactedOutsideRef) {
	const open = useStoreState(store, "open");
	const contentElement = useStoreState(store, "contentElement");
	const previousMouseDownRef = usePreviousMouseDownRef(open, contentElement ? getWindow(contentElement) : void 0);
	const focusedRef = useRef(false);
	useSafeLayoutEffect(() => {
		if (!open) return;
		if (!domReady) return;
		if (!contentElement) return;
		focusedRef.current = false;
		const onFocus = () => {
			focusedRef.current = true;
		};
		contentElement.addEventListener("focusin", onFocus, true);
		return () => contentElement.removeEventListener("focusin", onFocus, true);
	}, [
		open,
		domReady,
		contentElement
	]);
	const props = {
		store,
		capture: true,
		open,
		contentElement,
		focusedRef
	};
	useEventOutside({
		...props,
		type: "click",
		listener: (event) => {
			const { contentElement } = store.getState();
			const previousMouseDown = previousMouseDownRef.current;
			if (!previousMouseDown) return;
			if (!isElementMarked(previousMouseDown, contentElement?.id)) return;
			if (!shouldHideOnInteractOutside(hideOnInteractOutside, event)) return;
			if (interactedOutsideRef) interactedOutsideRef.current = true;
			store.hide();
		}
	});
	useEventOutside({
		...props,
		type: "focusin",
		listener: (event) => {
			const { contentElement } = store.getState();
			if (!contentElement) return;
			if (event.target === getDocument(contentElement)) return;
			if (!shouldHideOnInteractOutside(hideOnInteractOutside, event)) return;
			store.hide();
		}
	});
	useEventOutside({
		...props,
		type: "contextmenu",
		listener: (event) => {
			if (!shouldHideOnInteractOutside(hideOnInteractOutside, event)) return;
			if (interactedOutsideRef) interactedOutsideRef.current = true;
			store.hide();
		}
	});
}
//#endregion
export { useHideOnInteractOutside };

//# sourceMappingURL=use-hide-on-interact-outside.js.map